import type { BuyingProfile, MsalProfile } from '../../types';
import type { LDFlagSet } from 'launchdarkly-js-sdk-common';

import {
    browserName,
    deviceType,
    fullBrowserVersion,
    osName,
    osVersion,
} from 'react-device-detect';

const buyingRegEx = new RegExp(
    /^https?:\/\/buying(-dev|-test|)\.bestseller\.com(?:[/:?]|$)/i
);
const ambasRegEx = new RegExp(
    /^https?:\/\/ambassador\.buying01(-dev|-test|-prod)\.k8s\.bestcorp\.net$/i
);
const BUYING_BACKEND_ORIGIN =
    buyingRegEx.test(window.location.origin) ||
    ambasRegEx.test(window.location.origin)
        ? window.location.origin
        : 'https://ambassador.buying01-dev.k8s.bestcorp.net';
const SERVICE_NOW_ORIGIN_TEST = 'https://bestsellertest.service-now.com'; // We use test for all non-prod environments
const SERVICE_NOW_ORIGIN_PROD = 'https://bestseller.service-now.com';

interface GenerateWorkingNotesData {
    datadogSessionId?: string;
    datadogApplicationId?: string;
    buyingProfile?: BuyingProfile;
    msalProfile?: MsalProfile;
    featureFlags?: LDFlagSet;
}

const generateWorkingNotes = (data?: GenerateWorkingNotesData): string => {
    let newWorkingNotes = `
TECH SPECS:
device: ${deviceType}
operating system: ${osName}, v. ${osVersion}
browser: ${browserName}, v. ${fullBrowserVersion}
url: ${window.location.href}
resolution: ${window.innerWidth} x ${window.innerHeight}`;

    if (data?.buyingProfile ?? data?.msalProfile) {
        newWorkingNotes += '\n\nUSER DATA:';
        if (data.msalProfile) {
            newWorkingNotes += `
name: ${data.msalProfile.displayName}
title: ${data.msalProfile.jobTitle}
email: ${data.msalProfile.mail}`;
        }
        if (data.buyingProfile) {
            const brandsString = data.buyingProfile.brands
                .toSorted((a, b) => a.brandName.localeCompare(b.brandName))
                .map((b) => `\n  - ${b.brandName} (${b.brandNumber})`)
                .join('');
            const permissionsString = data.buyingProfile.permissions // Intentionally the raw profile, not useHasPermission — devbar simulation must never leak into tickets.
                .map(
                    (p) =>
                        `\n  - ${p.permissionName} \n    (${p.permissionNumber})`
                )
                .join('');
            newWorkingNotes += `
userNumber: ${data.buyingProfile.userNumber}
brands: ${brandsString || 'None'}
permissions: ${permissionsString || 'None'}`;
        }
    }

    // If featureFlags is provided, add them to the working notes
    if (data?.featureFlags) {
        const featureFlagString = Object.entries(data.featureFlags)
            .map(
                ([key, value]) =>
                    `\n  - ${key}\n    ${String(value).toUpperCase()}`
            )
            .join('');
        newWorkingNotes += `
\nLAUNCHDARKLY:
feature flags: ${featureFlagString}`;
    }

    // If datadogSessionId and datadogApplicationId are provided, add them to the working notes
    if (data?.datadogSessionId && data.datadogApplicationId) {
        newWorkingNotes += `
\nDATADOG:
session: https://bestseller.datadoghq.eu/rum/explorer?query=%40session.id%3A${data.datadogSessionId}%20%40application.id%3A${data.datadogApplicationId}&tab=action&from_ts=1626779189157&to_ts=1626865589157&live=true`;
    }

    return newWorkingNotes;
};

export {
    BUYING_BACKEND_ORIGIN,
    generateWorkingNotes,
    SERVICE_NOW_ORIGIN_PROD,
    SERVICE_NOW_ORIGIN_TEST,
};
export type { GenerateWorkingNotesData };
